import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLock, FiUnlock } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { usePurchasingSettingsSection } from "../usePurchasingSettingsSection";
import POApprovalDialog from "../popups/POApprovalDialog";
import "./OperationClosuresTab.css";

const API = "/api/purchasing/operation-closures";
const EMPTY_DATES = { closed_from: "", closed_until: "" };
const isDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && value >= "0001-01-01" && Number.isFinite(Date.parse(`${value}T00:00:00Z`))
  && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

function readClosures(data, businessUnitId) {
  if (!Array.isArray(data?.closures)) throw new Error("Missing closures response");
  const ids = new Set();
  return data.closures.map((closure) => {
    if (!closure || !Number.isSafeInteger(closure.id) || closure.id <= 0 || ids.has(closure.id)
      || Number(closure.business_unit_id) !== businessUnitId
      || ![closure.closed_from, closure.closed_until].every((date) => date === null || isDate(date))
      || (!closure.closed_from && !closure.closed_until)
      || (closure.closed_from && closure.closed_until && closure.closed_from > closure.closed_until)) {
      throw new Error("Invalid closures response");
    }
    ids.add(closure.id);
    return closure;
  });
}

export default function OperationClosuresTab() {
  const { themeId, languageId } = usePmsTheme();
  const es = languageId === "es";
  const t = (spanish, english) => es ? spanish : english;
  const { businessUnitId, canView, canEdit, context, isCurrent, requestConfig } = usePurchasingSettingsSection("operation_closures");
  const [saved, setSaved] = useState({ context: null, closures: [] });
  const [dates, setDates] = useState(EMPTY_DATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmReopen, setConfirmReopen] = useState(null);
  const version = useRef(0);
  const mutation = useRef(false);

  const load = useCallback(async () => {
    const requestVersion = ++version.current;
    setLoading(true); setError(null); setNotice(null); setSaved({ context: null, closures: [] });
    if (!businessUnitId || !canView || !isCurrent()) { setLoading(false); return; }
    try {
      const response = await axios.get(API, { ...requestConfig, params: { business_unit_id: businessUnitId } });
      if (requestVersion !== version.current || !isCurrent()) return;
      const closures = readClosures(response.data, businessUnitId);
      setDates(EMPTY_DATES); setSaved({ context, closures });
    } catch (_) {
      if (requestVersion === version.current && isCurrent()) setError("load");
    } finally {
      if (requestVersion === version.current) setLoading(false);
    }
  }, [businessUnitId, canView, context, isCurrent, requestConfig]);

  useEffect(() => {
    setDates(EMPTY_DATES); setSaving(false); setConfirmReopen(null); mutation.current = false;
    load();
    return () => { version.current += 1; };
  }, [load]);

  const ready = saved.context === context && !loading;
  const locked = !ready || !canEdit || saving;
  const isClosed = ready && saved.closures.length > 0;
  const datesValid = Boolean(dates.closed_from || dates.closed_until)
    && [dates.closed_from, dates.closed_until].every((date) => !date || isDate(date))
    && (!dates.closed_from || !dates.closed_until || dates.closed_from <= dates.closed_until);

  const save = async (reopenClosure = null) => {
    if (locked || mutation.current || !isCurrent("update") || (!reopenClosure && !datesValid)) return;
    if (reopenClosure && !saved.closures.some((closure) => closure.id === reopenClosure.id)) return;
    mutation.current = true;
    const requestVersion = version.current;
    const expectedDates = { closed_from: dates.closed_from || null, closed_until: dates.closed_until || null };
    setSaving(true); setError(null); setNotice(null);
    try {
      const response = reopenClosure
        ? await axios.delete(`${API}/${reopenClosure.id}`, { ...requestConfig, params: { business_unit_id: businessUnitId } })
        : await axios.post(API, { business_unit_id: businessUnitId, ...expectedDates }, requestConfig);
      if (requestVersion !== version.current || !isCurrent("update")) return;
      const closures = readClosures(response.data, businessUnitId);
      if (reopenClosure) {
        if (closures.some((closure) => closure.id === reopenClosure.id)) throw new Error("Reopening was not confirmed");
      } else {
        const [created] = readClosures({ closures: [response.data.closure] }, businessUnitId);
        if (!closures.some((closure) => closure.id === created.id && closure.closed_from === expectedDates.closed_from
          && closure.closed_until === expectedDates.closed_until)
          || created.closed_from !== expectedDates.closed_from || created.closed_until !== expectedDates.closed_until) {
          throw new Error("Closure was not confirmed");
        }
      }
      if (!reopenClosure) setDates(EMPTY_DATES);
      setSaved({ context, closures }); setConfirmReopen(null); setNotice(reopenClosure ? "reopened" : "saved");
    } catch (requestError) {
      if (requestVersion === version.current && isCurrent()) {
        // A failed response may follow a committed write; reload before showing a confirmed status.
        setError(requestError.response?.data?.code === "INVENTORY_OPERATION_CLOSURE_EXISTS" ? "duplicate" : "save");
        setSaved({ context: null, closures: [] }); setConfirmReopen(null);
      }
    } finally {
      if (requestVersion === version.current) { mutation.current = false; setSaving(false); }
    }
  };

  const formatDate = (date) => new Intl.DateTimeFormat(es ? "es-ES" : "en-GB", { timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
  const closedPeriod = (closure) => closure.closed_from && closure.closed_until
    ? t(`Del ${formatDate(closure.closed_from)} al ${formatDate(closure.closed_until)}, ambos incluidos.`, `From ${formatDate(closure.closed_from)} through ${formatDate(closure.closed_until)}, inclusive.`)
    : closure.closed_from ? t(`Desde el ${formatDate(closure.closed_from)}, incluido, sin fecha final.`, `From ${formatDate(closure.closed_from)}, inclusive, with no end date.`)
      : t(`Hasta el ${formatDate(closure.closed_until)}, incluido, sin fecha inicial.`, `Through ${formatDate(closure.closed_until)}, inclusive, with no start date.`);

  if (!canView) return <p role="alert">{t("No tienes permiso para acceder a esta sección.", "You don't have permission to access this section.")}</p>;

  return <section className="operationClosures pilcore-purchasing-ui" data-theme={themeId}>
    <header><h2><FiLock aria-hidden="true" />{t("Cierre de operaciones", "Operations closure")}</h2>
      <p>{t("Bloquea por fecha los movimientos físicos de stock del hotel activo: traspasos, entradas, salidas y ajustes de inventario.", "Close physical stock movements by date for the active hotel: transfers, receipts, issues and inventory adjustments.")}</p>
      <p>{t("Las demás operaciones siguen disponibles. Este cierre utiliza la fecha del movimiento físico y es independiente del cierre contable.", "Other operations remain available. This closure uses the physical movement date and is independent of accounting closure.")}</p>
    </header>
    {!businessUnitId ? <p role="alert">{t("Selecciona un hotel activo antes de continuar.", "Select an active hotel before continuing.")}</p>
      : loading ? <p role="status">{t("Cargando cierres…", "Loading closures…")}</p>
        : !ready ? <div className="operationClosures__warning"><p role="alert">{error === "duplicate"
          ? t("Ya existe un cierre con estas fechas. Vuelve a cargar los cierres para consultar su estado.", "A closure with these dates already exists. Reload the closures to see their current state.")
          : error === "save" ? t("No se ha podido confirmar el cambio. Vuelve a cargar los cierres para comprobar su estado antes de continuar.", "The change could not be confirmed. Reload the closures to check their status before continuing.")
            : t("No se han podido cargar los cierres. Su estado no está confirmado.", "The closures could not be loaded. Their status is unconfirmed.")}</p>
          <button type="button" onClick={load}>{t("Volver a cargar", "Reload")}</button></div>
          : <>
            <div className={`operationClosures__status ${isClosed ? "operationClosures__status--closed" : ""}`}>
              <strong>{isClosed ? t(`Cierres activos: ${saved.closures.length}`, `Active closures: ${saved.closures.length}`) : t("Sin cierre de movimientos", "No movement closure")}</strong>
              {isClosed ? <><ul className="operationClosures__list" aria-label={t("Cierres activos", "Active closures")}>
                {saved.closures.map((closure) => <li key={closure.id}>
                  <p>{closedPeriod(closure)}</p>
                  <button type="button" disabled={locked} aria-label={t(`Reabrir este cierre: ${closedPeriod(closure)}`, `Reopen this closure: ${closedPeriod(closure)}`)}
                    onClick={() => { if (isCurrent("update")) setConfirmReopen(closure); }}><FiUnlock aria-hidden="true" />{t("Reabrir este cierre", "Reopen this closure")}</button>
                </li>)}
              </ul><p className="operationClosures__help">{t("Cada cierre es independiente. Si se solapan, una fecha seguirá cerrada mientras la cubra otro cierre activo.", "Each closure is independent. If they overlap, a date remains closed while another active closure covers it.")}</p></>
                : <p>{t("No hay fechas bloqueadas por estos cierres.", "No dates are blocked by these closures.")}</p>}
            </div>
            {!canEdit && <p>{t("Puedes consultar los cierres, pero tu rol no permite modificarlos.", "You can view closures, but your role cannot change them.")}</p>}
            {notice && <p role="status" className="operationClosures__notice">{notice === "reopened"
              ? t("Cierre reabierto. Los demás cierres se mantienen.", "Closure reopened. Other closures remain in place.") : t("Cierre añadido.", "Closure added.")}</p>}
            <form onSubmit={(event) => { event.preventDefault(); save(); }}>
              <h3>{t("Añadir cierre", "Add closure")}</h3>
              <div className="operationClosures__dates">
                <label>{t("Desde (incluido)", "From (inclusive)")}<input type="date" min="0001-01-01" max="9999-12-31" value={dates.closed_from} disabled={locked}
                  onChange={(event) => { setDates({ ...dates, closed_from: event.target.value }); setNotice(null); }} /></label>
                <label>{t("Hasta (incluido)", "Through (inclusive)")}<input type="date" min="0001-01-01" max="9999-12-31" value={dates.closed_until} disabled={locked}
                  onChange={(event) => { setDates({ ...dates, closed_until: event.target.value }); setNotice(null); }} /></label>
              </div>
              <p className="operationClosures__help">{t("Completa solo «Hasta» para cerrar todas las fechas anteriores, solo «Desde» para cerrar a partir de esa fecha, o ambos campos para cerrar un intervalo. Las fechas indicadas se incluyen en el cierre. Puedes añadir tantos cierres como necesites.", "Fill in only “Through” to close all earlier dates, only “From” to close from that date onwards, or both fields to close a range. The selected dates are included. You can add as many closures as you need.")}</p>
              {dates.closed_from && dates.closed_until && dates.closed_from > dates.closed_until && <p role="alert">{t("La fecha inicial no puede ser posterior a la final.", "The start date cannot be later than the end date.")}</p>}
              {!dates.closed_from && !dates.closed_until && <p>{t("Indica al menos una fecha para añadir un cierre.", "Enter at least one date to add a closure.")}</p>}
              <div className="operationClosures__actions">
                <button type="submit" className="operationClosures__primary" disabled={locked || !datesValid}>{saving ? t("Guardando…", "Saving…") : t("Añadir cierre", "Add closure")}</button>
              </div>
            </form>
          </>}
    <POApprovalDialog isOpen={Boolean(confirmReopen && ready && canEdit)} busy={saving} onClose={() => setConfirmReopen(null)}
      title={t("Reabrir este cierre", "Reopen this closure")} footer={<>
        <button type="button" disabled={saving} onClick={() => setConfirmReopen(null)}>{t("Cancelar", "Cancel")}</button>
        <button type="button" disabled={saving} onClick={() => save(confirmReopen)}>{t("Confirmar reapertura", "Confirm reopening")}</button>
      </>}>
      {confirmReopen && <p><strong>{closedPeriod(confirmReopen)}</strong></p>}
      <p>{t("Se eliminará únicamente el cierre seleccionado del hotel activo. Los demás cierres se mantienen; una fecha seguirá bloqueada si otro cierre la cubre.", "Only the selected closure will be removed from the active hotel. Other closures remain in place; a date stays blocked if another closure covers it.")}</p>
    </POApprovalDialog>
  </section>;
}
